/**
 * UI-only mock campaign templates for local Templates tab without a backend.
 *
 * Enable in development only (production builds never use this file’s data; logic in `fetchAllCampaignTemplates` in `src/store/actions/marketing.action.ts`):
 * - `.env.local`: REACT_APP_USE_MARKETING_MOCK_DATA=true (unified), or REACT_APP_MOCK_MARKETING_TEMPLATES=true
 * - Or: localStorage.setItem('mockMarketingTemplates','1'); location.reload()
 * - Or enable `REACT_APP_MOCK_MARKETING_CAMPAIGNS` / `mockMarketingCampaigns` (includes templates).
 * - With a real API in dev: `REACT_APP_FORCE_LIVE_MARKETING_API=true` disables mocks.
 */
import birthdayHeroImageUrl from '@assets/images/Marketing/donut-candle.png'
import {
  CampaignTemplateChannel,
  CampaignTemplateType,
  ICampaignTemplate,
} from '@dataTypes/marketing.type'
import { getEnv } from '@utils/env'

function isoNow(): string {
  return new Date().toISOString()
}

type EmailMockVariant =
  | 'welcome'
  | 'birthday'
  | 'newsletter'
  | 'promotion'
  | 'outreach'
  | 'recall'
  | 'thank-you'
  | 'year-end'

/** Intentionally empty: was fake “window” dots above the card; removed for cleaner pre-defined templates. */
const WINDOW_BAR = ''

const FOOTER = `<footer class="mt-email-tpl__footer">
    <p class="mt-email-tpl__footer-line">123 Harbor St, Suite 200 · (555) 010-0200</p>
    <p class="mt-email-tpl__footer-links"><a href="#">Unsubscribe</a> · <a href="#">Email preferences</a></p>
  </footer>`

const FOOTER_CENTERED = `<footer class="mt-email-tpl__footer mt-email-tpl__footer--centered">
    <p class="mt-email-tpl__footer-line">123 Harbor St, Suite 200 · (555) 010-0200</p>
    <p class="mt-email-tpl__footer-links"><a href="#">Unsubscribe</a> · <a href="#">Email preferences</a></p>
  </footer>`

/** Inline tooth SVG (recall / welcome) — year-end template uses one large hero graphic */
const MOCK_TOOTH_SVG = `<svg class="mt-email-tpl__welcome-tooth" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill="currentColor" d="M12 2.2c-1.9 0-3.1 1.2-3.6 2.9-.3 1-.4 2-.5 3.1-.1 1.4-.3 2.8-.9 4.1-.4.9-.5 2.1-.2 3.1.3 1.1 1.1 1.8 2.1 1.8.8 0 1.4-.4 1.8-1 .3-.5.5-1.1.7-1.7.2-.6.4-1.1.9-1.4.5-.3 1.1-.2 1.6.1.4.3.6.8.8 1.3.2.7.4 1.4.8 2 .5.8 1.3 1.3 2.2 1.3 1.2 0 2.1-.9 2.4-2.1.2-.9 0-1.9-.3-2.8-.6-1.6-1-3.2-1.1-4.9l-.1-2.5c-.2-1.8-1.3-3.1-3.2-3.1-1 0-1.9.4-2.5 1.1-.6-.7-1.6-1.1-2.9-1.1Z"/></svg>`

/** Centered marketing-email column: logo, optional top image/hero, body, CTA, footer. */
function centeredLogoBlock(
  badge: string,
  brand: string,
  tagline?: string,
): string {
  const tag = tagline
    ? `<span class="mt-email-tpl__centered-tagline">${tagline}</span>`
    : ''
  return `<div class="mt-email-tpl__centered-logo-block">
    <span class="mt-email-tpl__centered-logo-badge">${badge}</span>
    <span class="mt-email-tpl__centered-brand-name">${brand}</span>
    ${tag}
  </div>`
}

function brandHeader(
  badge: string,
  brand: string,
  tagline?: string,
  compactClass = '',
): string {
  const tag = tagline
    ? `<span class="mt-email-tpl__header-tag">${tagline}</span>`
    : ''
  return `<header class="mt-email-tpl__header ${compactClass}">
    <div class="mt-email-tpl__logo-row">
      <span class="mt-email-tpl__logo-badge">${badge}</span>
      <div class="mt-email-tpl__brand-text">
        <span class="mt-email-tpl__logo-name">${brand}</span>
        ${tag}
      </div>
    </div>
  </header>`
}

/**
 * Each variant uses a different real-world pattern: header image, hero, letterhead, or text-only nav.
 */
function wrapEmailMockInner(
  variant: EmailMockVariant,
  mainHtml: string,
): string {
  const shell: Record<
    EmailMockVariant,
    { badge: string; brand: string; tagline?: string }
  > = {
    welcome: {
      badge: 'BS',
      brand: 'Bright Smile Dental',
      tagline: 'New patient welcome',
    },
    birthday: {
      badge: 'HB',
      brand: 'Birthday wishes',
      tagline: 'From your friends at Bright Smile',
    },
    newsletter: {
      badge: 'NL',
      brand: 'Bright Smile Dental',
      tagline: 'April 2026',
    },
    promotion: {
      badge: '%',
      brand: 'Bright Smile Deals',
      tagline: 'Limited time',
    },
    outreach: {
      badge: 'Hi',
      brand: 'Your care team',
      tagline: 'Bright Smile Dental',
    },
    recall: {
      badge: 'Rc',
      brand: 'Hygiene & scheduling',
      tagline: 'Bright Smile Dental',
    },
    'thank-you': {
      badge: 'TY',
      brand: 'Bright Smile Dental',
      tagline: 'Post-visit',
    },
    'year-end': {
      badge: 'EOY',
      brand: 'Year-end benefits',
      tagline: 'Bright Smile Dental',
    },
  }
  const { badge, brand, tagline } = shell[variant]
  const base = `mt-email-tpl mt-email-tpl--${variant}`

  switch (variant) {
    case 'welcome':
      return `<div class="${base} ${base}--layout-centered">
  ${WINDOW_BAR}
  <div class="mt-email-tpl__centered-outer">
    <div class="mt-email-tpl__centered-card mt-email-tpl__centered-card--welcome-email">
      <div class="mt-email-tpl__content mt-email-tpl__content--centered mt-email-tpl__content--welcome-shell">${mainHtml}</div>
    </div>
  </div>
  ${FOOTER_CENTERED}
</div>`

    case 'birthday':
      return `<div class="${base} ${base}--layout-centered">
  ${WINDOW_BAR}
  <div class="mt-email-tpl__centered-outer">
    <div class="mt-email-tpl__centered-card mt-email-tpl__centered-card--birthday-email">
      <div class="mt-email-tpl__content mt-email-tpl__content--centered mt-email-tpl__content--birthday-shell">${mainHtml}</div>
    </div>
  </div>
  ${FOOTER_CENTERED}
</div>`

    case 'newsletter': {
      const when = tagline || 'Practice update'
      return `<div class="${base} ${base}--layout-newsletter-minimal">
  ${WINDOW_BAR}
  <div class="mt-email-tpl__newsletter-minimal-shell">
    <div class="mt-email-tpl__content mt-email-tpl__content--newsletter-minimal">
      <p class="mt-email-tpl__newsletter-minimal-brand">${brand}</p>
      <p class="mt-email-tpl__newsletter-minimal-when">${when}</p>
      <div class="mt-email-tpl__newsletter-minimal-rule" aria-hidden="true"></div>
      ${mainHtml}
    </div>
  </div>
  ${FOOTER_CENTERED}
</div>`
    }

    case 'promotion':
      return `<div class="${base} ${base}--layout-promo-simple">
  ${WINDOW_BAR}
  <div class="mt-email-tpl__promo-shell">
    <div class="mt-email-tpl__content mt-email-tpl__content--promo">${mainHtml}</div>
  </div>
  ${FOOTER_CENTERED}
</div>`

    case 'outreach':
      return `<div class="${base} ${base}--layout-outreach-simple">
  ${WINDOW_BAR}
  <div class="mt-email-tpl__outreach-shell">
    <div class="mt-email-tpl__content mt-email-tpl__content--outreach">${mainHtml}</div>
  </div>
  ${FOOTER_CENTERED}
</div>`

    case 'recall':
      return `<div class="${base} ${base}--layout-recall-simple">
  ${WINDOW_BAR}
  <div class="mt-email-tpl__recall-shell">
    <div class="mt-email-tpl__recall-header">Appointment Reminder</div>
    <div class="mt-email-tpl__content mt-email-tpl__content--recall">${mainHtml}</div>
  </div>
  ${FOOTER_CENTERED}
</div>`

    case 'thank-you':
      return `<div class="${base} ${base}--layout-thank-you-simple">
  ${WINDOW_BAR}
  <div class="mt-email-tpl__thank-you-shell">
    <div class="mt-email-tpl__thank-you-header">Thank you for visiting</div>
    <div class="mt-email-tpl__content mt-email-tpl__content--thank-you">${mainHtml}</div>
  </div>
  ${FOOTER_CENTERED}
</div>`

    case 'year-end':
      return `<div class="${base} ${base}--layout-year-end-simple">
  ${WINDOW_BAR}
  <div class="mt-email-tpl__year-end-shell">
    <div class="mt-email-tpl__content mt-email-tpl__content--year-end">${mainHtml}</div>
  </div>
  ${FOOTER_CENTERED}
</div>`
  }
}

function buildMockEmailTemplates(t: string, by: string): ICampaignTemplate[] {
  return [
    {
      campaign_template_id: 'mock-email-tpl-welcome',
      channel: CampaignTemplateChannel.email,
      name: 'Welcome',
      description: 'Warm onboarding for new patients',
      type: CampaignTemplateType.predefined,
      text_content:
        'Welcome. We are excited to have you with us, {{Patient.FirstName}}. Book your first appointment when you are ready.',
      html_content: wrapEmailMockInner(
        'welcome',
        `<div class="mt-email-tpl__welcome-intro">
  <div class="mt-email-tpl__welcome-logo" aria-hidden="true" title="Click to add your logo" role="presentation">
    <span class="mt-email-tpl__welcome-logo-circle">
      <svg class="mt-email-tpl__welcome-tooth" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path fill="currentColor" d="M12 2.2c-1.9 0-3.1 1.2-3.6 2.9-.3 1-.4 2-.5 3.1-.1 1.4-.3 2.8-.9 4.1-.4.9-.5 2.1-.2 3.1.3 1.1 1.1 1.8 2.1 1.8.8 0 1.4-.4 1.8-1 .3-.5.5-1.1.7-1.7.2-.6.4-1.1.9-1.4.5-.3 1.1-.2 1.6.1.4.3.6.8.8 1.3.2.7.4 1.4.8 2 .5.8 1.3 1.3 2.2 1.3 1.2 0 2.1-.9 2.4-2.1.2-.9 0-1.9-.3-2.8-.6-1.6-1-3.2-1.1-4.9l-.1-2.5c-.2-1.8-1.3-3.1-3.2-3.1-1 0-1.9.4-2.5 1.1-.6-.7-1.6-1.1-2.9-1.1Z"/>
      </svg>
    </span>
  </div>
  <p class="mt-email-tpl__welcome-heading">Welcome.</p>
  <p class="mt-email-tpl__welcome-copy">We are excited to have you with us, <span class="e-mention-chip" data-value="Patient.FirstName">{{Patient.FirstName}}</span>. Our team is dedicated to making every visit comfortable and straightforward. When you are ready, we would love to help you book your first appointment.</p>
</div>
<p class="mt-email-tpl__btn-row mt-email-tpl__btn-row--welcome"><a href="https://example.com" data-mt-cta-slot="1" class="mt-email-tpl__btn mt-email-tpl__btn--welcome-cta">Book appointment</a></p>`,
      ),
      url: 'https://example.com/welcome',
      email_editor_canvas: {
        page_color: '#f8fafc',
        page_width: '520',
      },
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
    {
      campaign_template_id: 'mock-email-tpl-birthday',
      channel: CampaignTemplateChannel.email,
      name: 'Birthday greeting',
      description: 'Celebrate the patient’s day with a friendly note',
      type: CampaignTemplateType.predefined,
      text_content:
        'Happy Birthday! Get 50% every dental procedure this week. Plus 15% off select services — contact us to book.',
      html_content: wrapEmailMockInner(
        'birthday',
        `<p class="mt-email-tpl__birthday-title" style="font-size:60px;line-height:1.05;margin:0 0 10px;font-weight:800;">Happy Birthday!</p>
<p class="mt-email-tpl__birthday-subtitle" style="font-size:18px;margin:0 0 14px;font-weight:600;">Get 50% every dental procedure this week</p>
<div class="mt-email-tpl__birthday-figure"><img class="mt-email-tpl__birthday-img" src="${birthdayHeroImageUrl}" width="200" height="200" alt="Birthday" /></div>
<p class="mt-email-tpl__p mt-email-tpl__birthday-message">Happy birthday, <span class="e-mention-chip" data-value="Patient.FirstName">{{Patient.FirstName}}</span>! To celebrate your special day, we are offering <strong>15% off</strong> select dental services this week. Book your visit and mention this email — we cannot wait to see you smile.</p>
<div class="mt-email-tpl__btn-row"><a href="https://example.com" data-mt-cta-slot="1" class="mt-email-tpl__btn mt-email-tpl__btn--contact-teal">Contact Us</a></div>`,
      ),
      url: 'https://example.com/birthday',
      email_editor_canvas: {
        page_color: '#faf7f2',
        page_width: '520',
        cta_button_bg_color: '#78350f',
        cta_button_text_color: '#ffffff',
      },
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
    {
      campaign_template_id: 'mock-email-tpl-newsletter',
      channel: CampaignTemplateChannel.email,
      name: 'Newsletter',
      description: 'A clean patient update you can extend with Elements',
      type: CampaignTemplateType.predefined,
      text_content:
        'Bright Smile Dental — April 2026. Short note to patients; add sections from Elements as you like.',
      html_content: wrapEmailMockInner(
        'newsletter',
        `<p class="mt-email-tpl__p">Hi <span class="e-mention-chip" data-value="Patient.FirstName">{{Patient.FirstName}}</span>,</p>
<p class="mt-email-tpl__p">Here is a brief update from our practice. We will share tips, scheduling notes, and team news here—edit this text or drop in blocks from Elements.</p>
<p class="mt-email-tpl__p">Thanks for being with us,<br />Bright Smile Dental</p>`,
      ),
      url: 'https://example.com/newsletter',
      email_editor_canvas: {
        page_color: '#fafafa',
        page_width: '600',
      },
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
    {
      campaign_template_id: 'mock-email-tpl-promotion',
      channel: CampaignTemplateChannel.email,
      name: 'Promotion & offers',
      description: 'Highlight specials and limited-time deals',
      type: CampaignTemplateType.predefined,
      text_content:
        'Promotion & Offers: Teeth Whitening, New Patient Special, Referral Program, and insurance reminders.',
      html_content: wrapEmailMockInner(
        'promotion',
        `<p class="mt-email-tpl__promo-page-title">Promotion &amp; Offers</p>
<div class="mt-email-tpl__promo-list">
  <div class="mt-email-tpl__promo-row">
    <div class="mt-email-tpl__promo-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4"/><path d="M22 5h-4"/><path d="M4 17v2"/><path d="M5 18H3"/></svg></div>
    <div class="mt-email-tpl__promo-copy">
      <p class="mt-email-tpl__promo-item-title">Teeth Whitening Promo</p>
      <p class="mt-email-tpl__promo-item-desc">Save on professional whitening this month. Book a quick consult and we will match you with the right option.</p>
    </div>
  </div>
  <div class="mt-email-tpl__promo-row">
    <div class="mt-email-tpl__promo-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v10H4V12"/><path d="M22 7H2v5h20z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg></div>
    <div class="mt-email-tpl__promo-copy">
      <p class="mt-email-tpl__promo-item-title">New Patient Special</p>
      <p class="mt-email-tpl__promo-item-desc">Welcome offer for first-time patients—ask the front desk what is included with your initial visit.</p>
    </div>
  </div>
  <div class="mt-email-tpl__promo-row">
    <div class="mt-email-tpl__promo-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></div>
    <div class="mt-email-tpl__promo-copy">
      <p class="mt-email-tpl__promo-item-title">Referral Program</p>
      <p class="mt-email-tpl__promo-item-desc">Refer a friend or family member and enjoy a thank-you perk when they complete their first appointment.</p>
    </div>
  </div>
  <div class="mt-email-tpl__promo-row">
    <div class="mt-email-tpl__promo-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg></div>
    <div class="mt-email-tpl__promo-copy">
      <p class="mt-email-tpl__promo-item-title">End-of-year Insurance Reminder</p>
      <p class="mt-email-tpl__promo-item-desc">Many benefits reset January 1. Use remaining coverage for eligible treatment before the year ends.</p>
    </div>
  </div>
</div>`,
      ),
      url: 'https://example.com/promo',
      email_editor_canvas: {
        page_color: '#f9f9f9',
        page_width: '600',
      },
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
    {
      campaign_template_id: 'mock-email-tpl-outreach',
      channel: CampaignTemplateChannel.email,
      name: 'Patient outreach',
      description: 'Gentle check-in for patients you have not seen lately',
      type: CampaignTemplateType.predefined,
      text_content:
        'WE MISS YOU — Hi {{Patient.FirstName}}, it has been a while. Schedule now, call us, or reply with questions. Thank you, Bright Smile Dental.',
      html_content: wrapEmailMockInner(
        'outreach',
        `<p class="mt-email-tpl__outreach-headline">WE MISS YOU</p>
<p class="mt-email-tpl__outreach-sub">It's been a while since your last visit.</p>
<p class="mt-email-tpl__outreach-body">Hi <span class="e-mention-chip" data-value="Patient.FirstName">{{Patient.FirstName}}</span>, we'd love to see you back at Bright Smile Dental. Whether you are due for a cleaning or want to catch up on treatment, we will help you find a time that fits your schedule.</p>
<div class="mt-email-tpl__outreach-actions">
  <p class="mt-email-tpl__btn-row mt-email-tpl__outreach-btn-row"><a href="https://example.com" data-mt-cta-slot="1" class="mt-email-tpl__btn mt-email-tpl__btn--outreach-primary">Schedule Now</a></p>
  <p class="mt-email-tpl__btn-row mt-email-tpl__outreach-btn-row"><a href="https://example.com" data-mt-cta-slot="2" class="mt-email-tpl__btn mt-email-tpl__btn--outreach-secondary">Call us</a></p>
</div>
<p class="mt-email-tpl__outreach-note">If you have any questions, reply to this email or call the office—we are happy to help.</p>
<p class="mt-email-tpl__outreach-signoff">Thank you,<br /><span class="mt-email-tpl__outreach-practice">Bright Smile Dental</span></p>`,
      ),
      url: 'https://example.com/outreach',
      email_editor_canvas: {
        page_color: '#ffffff',
        page_width: '600',
      },
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
    {
      campaign_template_id: 'mock-email-tpl-recall',
      channel: CampaignTemplateChannel.email,
      name: 'Appointment & recall',
      description: 'Appointment reminder with date, time, and provider',
      type: CampaignTemplateType.predefined,
      text_content:
        'Hi {{Patient.FirstName}}, reminder: your appointment on Thu Jun 12, 2026 at 2:00 PM with Dr. Smith. View or reschedule online.',
      html_content: wrapEmailMockInner(
        'recall',
        `<div class="mt-email-tpl__recall-logo" aria-hidden="true" title="Click to add your logo" role="presentation">
  <span class="mt-email-tpl__recall-logo-circle">
    <svg class="mt-email-tpl__welcome-tooth" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="currentColor" d="M12 2.2c-1.9 0-3.1 1.2-3.6 2.9-.3 1-.4 2-.5 3.1-.1 1.4-.3 2.8-.9 4.1-.4.9-.5 2.1-.2 3.1.3 1.1 1.1 1.8 2.1 1.8.8 0 1.4-.4 1.8-1 .3-.5.5-1.1.7-1.7.2-.6.4-1.1.9-1.4.5-.3 1.1-.2 1.6.1.4.3.6.8.8 1.3.2.7.4 1.4.8 2 .5.8 1.3 1.3 2.2 1.3 1.2 0 2.1-.9 2.4-2.1.2-.9 0-1.9-.3-2.8-.6-1.6-1-3.2-1.1-4.9l-.1-2.5c-.2-1.8-1.3-3.1-3.2-3.1-1 0-1.9.4-2.5 1.1-.6-.7-1.6-1.1-2.9-1.1Z"/>
    </svg>
  </span>
</div>
<p class="mt-email-tpl__recall-greeting">Hi <span class="e-mention-chip" data-value="Patient.FirstName">{{Patient.FirstName}}</span>,</p>
<p class="mt-email-tpl__recall-intro">This is a reminder for your upcoming appointment.</p>
<div class="mt-email-tpl__recall-details">
  <div class="mt-email-tpl__recall-detail-row"><span class="mt-email-tpl__recall-detail-label">Date</span><span class="mt-email-tpl__recall-detail-value">Thursday, June 12, 2026</span></div>
  <div class="mt-email-tpl__recall-detail-row"><span class="mt-email-tpl__recall-detail-label">Time</span><span class="mt-email-tpl__recall-detail-value">2:00 PM</span></div>
  <div class="mt-email-tpl__recall-detail-row"><span class="mt-email-tpl__recall-detail-label">Dentist</span><span class="mt-email-tpl__recall-detail-value">Dr. Jordan Smith</span></div>
</div>
<div class="mt-email-tpl__btn-row mt-email-tpl__recall-btn-row"><a href="https://example.com" data-mt-cta-slot="1" class="mt-email-tpl__btn mt-email-tpl__btn--recall-primary">View or reschedule</a></div>
<p class="mt-email-tpl__recall-note">Please contact us if you need to change your schedule.</p>`,
      ),
      url: 'https://example.com/recall',
      email_editor_canvas: {
        page_color: '#ffffff',
        page_width: '600',
      },
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
    {
      campaign_template_id: 'mock-email-tpl-thank-you',
      channel: CampaignTemplateChannel.email,
      name: 'Thank you / post-visit',
      description: 'After-visit gratitude with optional review CTA',
      type: CampaignTemplateType.predefined,
      text_content:
        'Thanks for visiting Bright Smile Dental today, {{Patient.FirstName}}. We appreciate you — leave a review if you can.',
      html_content: wrapEmailMockInner(
        'thank-you',
        `<div class="mt-email-tpl__recall-logo" aria-hidden="true" title="Click to add your logo" role="presentation">
  <span class="mt-email-tpl__recall-logo-circle">
    <svg class="mt-email-tpl__welcome-tooth" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="currentColor" d="M12 2.2c-1.9 0-3.1 1.2-3.6 2.9-.3 1-.4 2-.5 3.1-.1 1.4-.3 2.8-.9 4.1-.4.9-.5 2.1-.2 3.1.3 1.1 1.1 1.8 2.1 1.8.8 0 1.4-.4 1.8-1 .3-.5.5-1.1.7-1.7.2-.6.4-1.1.9-1.4.5-.3 1.1-.2 1.6.1.4.3.6.8.8 1.3.2.7.4 1.4.8 2 .5.8 1.3 1.3 2.2 1.3 1.2 0 2.1-.9 2.4-2.1.2-.9 0-1.9-.3-2.8-.6-1.6-1-3.2-1.1-4.9l-.1-2.5c-.2-1.8-1.3-3.1-3.2-3.1-1 0-1.9.4-2.5 1.1-.6-.7-1.6-1.1-2.9-1.1Z"/>
    </svg>
  </span>
</div>
<p class="mt-email-tpl__recall-greeting">Hi <span class="e-mention-chip" data-value="Patient.FirstName">{{Patient.FirstName}}</span>,</p>
<p class="mt-email-tpl__recall-intro">Thank you for visiting Bright Smile Dental today. We hope your visit was comfortable and that any next steps feel clear.</p>
<p class="mt-email-tpl__recall-intro">If you have a moment, a short review helps other families find care — we read every one.</p>
<div class="mt-email-tpl__btn-row mt-email-tpl__recall-btn-row"><a href="https://example.com" data-mt-cta-slot="1" class="mt-email-tpl__btn mt-email-tpl__btn--recall-primary">Leave a review</a></div>
<p class="mt-email-tpl__recall-note">Questions? Reply to this email or call the office anytime.</p>`,
      ),
      url: 'https://example.com/thank-you',
      email_editor_canvas: {
        page_color: '#ffffff',
        page_width: '600',
      },
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
    {
      campaign_template_id: 'mock-email-tpl-saved-year-end',
      channel: CampaignTemplateChannel.email,
      name: 'Year End Promo',
      description: 'Custom saved template — benefits deadline reminder',
      type: CampaignTemplateType.saved,
      text_content:
        'Use your remaining insurance benefits before they reset. Ask about our year-end treatment bundle.',
      html_content: wrapEmailMockInner(
        'year-end',
        `<p class="mt-email-tpl__year-end-kicker">Before December 31</p>
<p class="mt-email-tpl__year-end-title">Use your remaining benefits</p>
<p class="mt-email-tpl__year-end-subtitle">Insurance and FSA dollars often reset January 1</p>
<p class="mt-email-tpl__year-end-desc">Hi <span class="e-mention-chip" data-value="Patient.FirstName">{{Patient.FirstName}}</span>, many plans refresh at the new year. Book any eligible visits or treatment before your deadline and ask us how to make the most of what you have left.</p>
<div class="mt-email-tpl__year-end-hero" aria-hidden="true" title="Click to add your logo" role="presentation">
  <span class="mt-email-tpl__year-end-hero-circle">${MOCK_TOOTH_SVG}</span>
</div>
<div class="mt-email-tpl__year-end-services">
  <div class="mt-email-tpl__year-end-svc-row"><span class="mt-email-tpl__year-end-svc-dot"></span><span>Exams, cleanings &amp; fluoride</span></div>
  <div class="mt-email-tpl__year-end-svc-row"><span class="mt-email-tpl__year-end-svc-dot"></span><span>Fillings and other covered treatment</span></div>
  <div class="mt-email-tpl__year-end-svc-row"><span class="mt-email-tpl__year-end-svc-dot"></span><span>Whitening or elective care (plan rules vary)</span></div>
</div>
<div class="mt-email-tpl__btn-row"><a href="https://example.com" data-mt-cta-slot="1" class="mt-email-tpl__btn mt-email-tpl__btn--year-end-cta">Book appointment</a></div>`,
      ),
      url: 'https://example.com/year-end',
      email_editor_canvas: {
        page_color: '#f8fafc',
        page_width: '600',
      },
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
  ]
}

/** Predefined email starters merged with API templates in `fetchAllCampaignTemplates` (dev + production). */
export function getMockEmailTemplates(): ICampaignTemplate[] {
  return buildMockEmailTemplates(isoNow(), 'mock-user')
}

export function getMockCampaignTemplates(): ICampaignTemplate[] {
  if (getEnv('NODE_ENV') === 'production') {
    console.error(
      '[Marketing] getMockCampaignTemplates must not run in production builds; returning [].',
    )
    return []
  }
  const t = isoNow()
  const by = 'mock-user'

  const smsTemplates: ICampaignTemplate[] = [
    {
      campaign_template_id: 'mock-tpl-pre-appointment',
      channel: CampaignTemplateChannel.sms,
      name: 'Appointment reminder',
      description: 'Same-day visit reminder with confirm keyword',
      type: CampaignTemplateType.predefined,
      text_content:
        'Bright Smile Dental\n\nHi {{Patient.FirstName}}, reminder: your visit is today at 2:00 PM. Reply C to confirm.',
      html_content: `<p style="margin:0 0 6px;font-weight:600;font-size:1em;">Bright Smile Dental</p><p style="margin:0;line-height:1.35;">Hi <span class="e-mention-chip" data-value="Patient.FirstName">{{Patient.FirstName}}</span>, reminder: your visit is <strong>today at 2:00 PM</strong>. Reply <strong>C</strong> to confirm.</p>`,
      url: 'https://example.com/confirm',
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
    {
      campaign_template_id: 'mock-tpl-pre-recall',
      channel: CampaignTemplateChannel.sms,
      name: 'Recall / recare',
      description: 'Encourage patients to book their next cleaning',
      type: CampaignTemplateType.predefined,
      text_content:
        "Hi {{Patient.FirstName}}, it's been a while since your last cleaning. Book online anytime.",
      html_content: `<p style="margin:0;line-height:1.35;">Hi <span class="e-mention-chip" data-value="Patient.FirstName">{{Patient.FirstName}}</span>, it's been a while since your last cleaning. Tap below to pick a time that works for you.</p><p style="margin:8px 0 0;"><a class="e-rte-anchor" href="#">Book now</a></p>`,
      url: 'https://example.com/recare',
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
    {
      campaign_template_id: 'mock-tpl-pre-review',
      channel: CampaignTemplateChannel.sms,
      name: 'Review request',
      description: 'Short thank-you plus link to leave a review',
      type: CampaignTemplateType.predefined,
      text_content:
        'Thanks for visiting us today! If you have a moment, we would love a quick review.',
      html_content: `<p style="margin:0 0 6px;line-height:1.35;">Thanks for visiting us today<span class="e-mention-chip" data-value="Patient.FirstName"> {{Patient.FirstName}}</span>!</p><p style="margin:0;line-height:1.35;">If you have a moment, we would love a quick review — it helps our small team a lot.</p>`,
      url: 'https://example.com/review',
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
    {
      campaign_template_id: 'mock-tpl-saved-birthday',
      channel: CampaignTemplateChannel.sms,
      name: 'Birthday offer',
      description: 'Custom saved template — seasonal promo',
      type: CampaignTemplateType.saved,
      text_content:
        'Happy birthday! Enjoy 15% off any whitening this month at our clinic.',
      html_content: `<p style="margin:0 0 6px;font-weight:600;">Happy birthday! 🎉</p><p style="margin:0;line-height:1.35;">Enjoy <strong>15% off</strong> any whitening this month. Show this message at checkout.</p><p style="margin:8px 0 0;font-size:0.92em;opacity:0.9;">— Enamel AI Demo Clinic</p>`,
      url: 'https://example.com/offers',
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
    {
      campaign_template_id: 'mock-tpl-saved-noshow',
      channel: CampaignTemplateChannel.sms,
      name: 'Missed appointment',
      description: 'Polite follow-up when a patient no-shows',
      type: CampaignTemplateType.saved,
      text_content:
        'We missed you today. Reply or call us to reschedule — we are here to help.',
      html_content: `<p style="margin:0;line-height:1.35;">Hi <span class="e-mention-chip" data-value="Patient.FirstName">{{Patient.FirstName}}</span>, we missed you at today's appointment.</p><p style="margin:8px 0 0;line-height:1.35;">Reply to this message or call us to reschedule — we're happy to find another time.</p>`,
      url: 'https://example.com/reschedule',
      created_by: by,
      date_time_created_at: t,
      date_time_updated_at: t,
    },
  ]

  return [...smsTemplates, ...buildMockEmailTemplates(t, by)]
}
